import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Avatar,
  Tooltip,
  Alert,
  Snackbar,
  Autocomplete,
  Tabs,
  Tab,
  Badge,
  CircularProgress,
  Divider,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControlLabel,
  Switch,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Cancel,
  Search,
  Phone,
  Email,
  LocationOn,
  Person,
  Business,
  Visibility,
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Payment,
  Schedule,
  CheckCircle,
  Pending,
  ExpandMore,
  AttachMoney,
  CalendarToday,
  Description,
  Diamond,
  EmojiEvents,
  AccountBalance,
  Receipt,
  Calculate,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Scale,
  Speed,
  MonetizationOn,
  LocalAtm,
  AccountBalanceWallet,
  SwapHoriz,
  Create
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import * as goldsmithActions from '../redux/actions/goldsmithActions';

const GoldsmithPurchases = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { 
    purchases, 
    purchasesLoading, 
    purchasesError,
    goldsmiths,
    goldRate,
    goldRateLoading
  } = useSelector(state => state.goldsmith);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [currentTab, setCurrentTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [formData, setFormData] = useState({
    goldsmith_id: null,
    purchase_date: new Date().toISOString().split('T')[0],
    net_weight: '',
    touch: '',
    gold_rate: '',
    making_charges: '',
    wastage_charges: '',
    other_charges: '',
    payment_mode: 'mixed',
    pure_given: '',
    amount_paid: '',
    notes: '',
    customer_order_id: null
  });

  const [calculations, setCalculations] = useState({
    pure_weight: 0,
    pure_amount: 0,
    total_amount: 0,
    balance_pure: 0,
    balance_amount: 0
  });

  useEffect(() => {
    dispatch(goldsmithActions.getGoldsmithPurchases());
    dispatch(goldsmithActions.getGoldsmiths());
    dispatch(goldsmithActions.getTodayGoldRate());
  }, [dispatch]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    dispatch(goldsmithActions.getGoldsmithPurchases({ search: e.target.value }));
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
    const params = { search: searchTerm };
    if (e.target.value !== 'all') {
      params.status = e.target.value;
    }
    dispatch(goldsmithActions.getGoldsmithPurchases(params));
  };

  const handleOpenDialog = (mode, purchase = null) => {
    setDialogMode(mode);
    if (mode === 'edit' || mode === 'view') {
      setFormData({
        goldsmith_id: purchase.goldsmith?.id || purchase.goldsmith_id || null,
        purchase_date: purchase.purchase_date || '',
        product_name: purchase.product_name || '',
        net_weight: purchase.net_weight || '',
        touch: purchase.touch || '',
        gold_rate: purchase.gold_rate || '',
        pure_amount: purchase.pure_amount || '',
        mc_wastage_amount: purchase.mc_wastage_amount || '',
        making_charges: purchase.making_charges || '',
        wastage_charges: purchase.wastage_charges || '',
        other_charges: purchase.other_charges || '',
        payment_mode: purchase.payment_mode || 'mixed',
        pure_given: purchase.payment_details?.pure_given || '',
        amount_paid: purchase.payment_details?.amount_paid || '',
        balance_pure: purchase.payment_details?.balance_pure || '',
        balance_amount: purchase.payment_details?.balance_amount || '',
        notes: purchase.notes || '',
        customer_order_id: purchase.customer_order_id || null
      });
      setSelectedPurchase(purchase);
      // Populate calculations from the purchase or compute if missing
      const netWeightNum = parseFloat(purchase.net_weight) || 0;
      const touchNum = parseFloat(purchase.touch) || 0;
      const goldRateNum = parseFloat(purchase.gold_rate) || 0;
      const makingChargesNum = parseFloat(purchase.making_charges || 0) || 0;
      const wastageChargesNum = parseFloat(purchase.wastage_charges || 0) || 0;
      const otherChargesNum = parseFloat(purchase.other_charges || 0) || 0;
      const pureGivenNum = parseFloat(purchase.payment_details?.pure_given || purchase.pure_given || 0) || 0;
      const amountPaidNum = parseFloat(purchase.payment_details?.amount_paid || purchase.amount_paid || 0) || 0;

      const computedPureWeight = (netWeightNum * touchNum) / 100;
      const pureWeight = typeof purchase.pure_weight === 'number' ? purchase.pure_weight : computedPureWeight;
      const computedPureAmount = pureWeight * goldRateNum;
      const pureAmount = typeof purchase.pure_amount === 'number' ? purchase.pure_amount : computedPureAmount;
      const computedTotalAmount = pureAmount + makingChargesNum + wastageChargesNum + otherChargesNum;
      const totalAmount = typeof purchase.total_amount === 'number' ? purchase.total_amount : computedTotalAmount;

      let balancePureNum = 0;
      let balanceAmountNum = 0;
      if ((purchase.payment_mode || 'mixed') === 'pure_only') {
        balancePureNum = pureWeight - pureGivenNum;
      } else if (purchase.payment_mode === 'amount_only') {
        balanceAmountNum = totalAmount - amountPaidNum;
      } else {
        const pureGivenValue = pureGivenNum * goldRateNum;
        balanceAmountNum = totalAmount - pureGivenValue - amountPaidNum;
        balancePureNum = pureWeight - pureGivenNum;
      }
      setCalculations({
        pure_weight: pureWeight,
        pure_amount: pureAmount,
        total_amount: totalAmount,
        balance_pure: balancePureNum,
        balance_amount: balanceAmountNum
      });
    } else {
      setFormData({
        goldsmith_id: null,
        purchase_date: new Date().toISOString().split('T')[0],
        product_name: '',
        net_weight: '',
        touch: '',
        gold_rate: goldRate.toString(),
        pure_amount: '',
        mc_wastage_amount: '',
        making_charges: '',
        wastage_charges: '',
        other_charges: '',
        payment_mode: 'mixed',
        pure_given: '',
        amount_paid: '',
        balance_pure: '',
        balance_amount: '',
        notes: '',
        customer_order_id: null
      });
      setSelectedPurchase(null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedPurchase(null);
    setCurrentTab(0);
    setCalculations({
      pure_weight: 0,
      pure_amount: 0,
      total_amount: 0,
      balance_pure: 0,
      balance_amount: 0
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Recalculate when relevant fields change
    if (['net_weight', 'touch', 'gold_rate', 'making_charges', 'wastage_charges', 'other_charges', 'payment_mode', 'pure_given', 'amount_paid'].includes(name)) {
      setTimeout(calculateAmounts, 100);
    }
  };

  const handleGoldsmithChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      goldsmith_id: newValue?.id || null
    }));
  };

  const calculateAmounts = () => {
    const netWeight = parseFloat(formData.net_weight) || 0;
    const touch = parseFloat(formData.touch) || 0;
    const goldRate = parseFloat(formData.gold_rate) || 0;
    const makingCharges = parseFloat(formData.making_charges) || 0;
    const wastageCharges = parseFloat(formData.wastage_charges) || 0;
    const otherCharges = parseFloat(formData.other_charges) || 0;
    const pureGiven = parseFloat(formData.pure_given) || 0;
    const amountPaid = parseFloat(formData.amount_paid) || 0;

    // Calculate pure weight
    const pureWeight = (netWeight * touch) / 100;
    
    // Calculate pure amount
    const pureAmount = pureWeight * goldRate;
    
    // Calculate total amount
    const totalAmount = pureAmount + makingCharges + wastageCharges + otherCharges;

    let balancePure = 0;
    let balanceAmount = 0;

    // Calculate balance based on payment mode
    if (formData.payment_mode === 'pure_only') {
      balancePure = pureWeight - pureGiven;
    } else if (formData.payment_mode === 'amount_only') {
      balanceAmount = totalAmount - amountPaid;
    } else if (formData.payment_mode === 'mixed') {
      const pureGivenValue = pureGiven * goldRate;
      balanceAmount = totalAmount - pureGivenValue - amountPaid;
      balancePure = pureWeight - pureGiven;
    }

    setCalculations({
      pure_weight: pureWeight,
      pure_amount: pureAmount,
      total_amount: totalAmount,
      balance_pure: balancePure,
      balance_amount: balanceAmount
    });
  };

  const handleSubmit = async () => {
    try {
      const purchaseData = {
        ...formData,
        ...calculations
      };

      if (dialogMode === 'create') {
        await dispatch(goldsmithActions.createGoldsmithPurchase(purchaseData));
        setSnackbar({ open: true, message: 'Purchase created successfully!', severity: 'success' });
      } else if (dialogMode === 'edit') {
        await dispatch(goldsmithActions.updateGoldsmithPurchase(selectedPurchase.id, purchaseData));
        setSnackbar({ open: true, message: 'Purchase updated successfully!', severity: 'success' });
      }
      handleCloseDialog();
    } catch (error) {
      setSnackbar({ open: true, message: error.message || 'An error occurred', severity: 'error' });
    }
  };

  const handleCancelPurchase = (purchase) => {
    setSelectedPurchase(purchase);
    setCancelDialogOpen(true);
  };

  const handleCancelConfirm = async () => {
    if (selectedPurchase) {
      try {
        // TODO: Implement cancel purchase API call
        console.log('Cancelling purchase:', selectedPurchase.id);
        
        // For now, just show success message
        setSnackbar({ 
          open: true, 
          message: `Purchase #${selectedPurchase.id} cancelled successfully!`, 
          severity: 'success' 
        });
        
        setCancelDialogOpen(false);
        setSelectedPurchase(null);
        
        // Refresh the purchases list
        dispatch(goldsmithActions.getGoldsmithPurchases());
      } catch (error) {
        setSnackbar({ 
          open: true, 
          message: 'Failed to cancel purchase. Please try again.', 
          severity: 'error' 
        });
      }
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      paid: 'success',
      partial: 'info',
      completed: 'success'
    };
    return colors[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const icons = {
      pending: <Pending />,
      paid: <CheckCircle />,
      partial: <TrendingUp />,
      completed: <CheckCircle />
    };
    return icons[status] || <Pending />;
  };

  const getPaymentModeIcon = (mode) => {
    const icons = {
      pure_only: <Diamond />,
      amount_only: <LocalAtm />,
      mixed: <SwapHoriz />
    };
    return icons[mode] || <Payment />;
  };

  const formatCurrency = (amount) => {
    const roundedAmount = Math.round(amount || 0);
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(roundedAmount);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const purchaseList = Array.isArray(purchases) ? purchases : [];
  const filteredPurchases = purchaseList.filter(purchase => {
    const matchesSearch = purchase.purchase_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         purchase.notes?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         purchase.goldsmith?.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || purchase.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statusOptions = [
    { value: 'all', label: 'All Purchases' },
    { value: 'pending', label: 'Pending' },
    { value: 'paid', label: 'Paid' },
    { value: 'partial', label: 'Partial' },
    { value: 'completed', label: 'Completed' }
  ];

  const paymentModes = [
    { value: 'pure_only', label: 'Pure Only', description: 'Pay entirely in pure gold' },
    { value: 'amount_only', label: 'Amount Only', description: 'Pay entirely in cash' },
    { value: 'mixed', label: 'Mixed Payment', description: 'Pay partial pure + cash' }
  ];

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1976d2' }}>
            <ShoppingCart sx={{ mr: 2, verticalAlign: 'middle' }} />
            Goldsmith Purchases
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage goldsmith purchases and payments
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Create />}
          onClick={() => navigate('/new-goldsmith-purchase')}
          sx={{
            background: 'linear-gradient(45deg, #FF9800 30%, #FFC107 90%)',
            boxShadow: '0 3px 5px 2px rgba(255, 152, 0, .3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #F57C00 30%, #FFB300 90%)',
            }
          }}
        >
          Create New Purchase
        </Button>
      </Box>

      {/* Filters */}
      <Card sx={{ mb: 3, borderRadius: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="Search purchases..."
                value={searchTerm}
                onChange={handleSearch}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  onChange={handleStatusFilter}
                  label="Status"
                  sx={{ borderRadius: 2 }}
                >
                  {statusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  icon={<ShoppingCart />}
                  label={`${filteredPurchases.length} Purchases`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Purchases Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 20px rgba(0,0,0,0.1)' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>Purchase Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Goldsmith</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Weight & Touch</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Pure Details</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Summary</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Payment Mode</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {purchasesLoading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : filteredPurchases.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      No purchases found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredPurchases.map((purchase) => (
                  <TableRow key={purchase.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body1" fontWeight="bold" color="primary">
                          {purchase.purchase_number}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(purchase.purchase_date)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#ff9800', width: 32, height: 32 }}>
                          <Business />
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {purchase.goldsmith?.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {purchase.goldsmith?.contact_person}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {purchase.net_weight}g
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {purchase.touch} Touch
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {purchase.pure_weight}g
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ₹{purchase.gold_rate}/g
                        </Typography>
                        {Array.isArray(purchase.items) && purchase.items.length > 0 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {purchase.items.length} item{purchase.items.length > 1 ? 's' : ''}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' }, gap: 1 }}>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Total Pure</Typography>
                          <Typography variant="body2" fontWeight="bold">{Number(purchase.pure_weight || 0).toFixed(3)}g</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Paid Pure</Typography>
                          <Typography variant="body2" fontWeight="bold">{Number((purchase.payment_details?.pure_given) ?? purchase.pure_given ?? 0).toFixed(3)}g</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Balance Pure</Typography>
                          <Typography variant="body2" fontWeight="bold">{Number((purchase.payment_details?.balance_pure) ?? purchase.balance_pure ?? Math.max(Number(purchase.pure_weight || 0) - Number((purchase.payment_details?.pure_given) ?? purchase.pure_given ?? 0), 0)).toFixed(3)}g</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Amount</Typography>
                          <Typography variant="body2" fontWeight="bold">{formatCurrency(Number(purchase.total_amount || 0))}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Paid Amount</Typography>
                          <Typography variant="body2" fontWeight="bold">{formatCurrency(Number((purchase.payment_details?.amount_paid) ?? purchase.amount_paid ?? 0))}</Typography>
                        </Box>
                        <Box>
                          <Typography variant="caption" color="text.secondary">Balance Amount</Typography>
                          <Typography variant="body2" fontWeight="bold">{formatCurrency(((purchase.payment_details?.balance_amount) ?? purchase.balance_amount ?? Math.max(Number(purchase.total_amount || 0) - (Number((purchase.payment_details?.pure_given) ?? purchase.pure_given ?? 0) * Number(purchase.gold_rate || 0)) - Number((purchase.payment_details?.amount_paid) ?? purchase.amount_paid ?? 0), 0)))}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getPaymentModeIcon(purchase.payment_mode)}
                        label={(purchase.payment_mode || 'UNKNOWN').toString().replace('_', ' ').toUpperCase()}
                        color="info"
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(purchase.status)}
                        label={purchase.status.toUpperCase()}
                        color={getStatusColor(purchase.status)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('view', purchase)}
                            color="info"
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            onClick={() => handleOpenDialog('edit', purchase)}
                            color="primary"
                          >
                            <Edit />
                          </IconButton>
                        </Tooltip>
                        {purchase.status !== 'cancelled' && (
                          <Tooltip title="Cancel Purchase">
                            <IconButton
                              size="small"
                              onClick={() => handleCancelPurchase(purchase)}
                              color="error"
                            >
                              <Cancel />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Purchase Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
          color: 'white',
          p: 3,
          position: 'sticky',
          top: 0,
          zIndex: 1
        }}>
          <Typography variant="h5" fontWeight="bold">
            {dialogMode === 'create' && 'Create New Purchase'}
            {dialogMode === 'edit' && 'Edit Purchase'}
            {dialogMode === 'view' && 'Purchase Details'}
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={goldsmiths || []}
                getOptionLabel={(option) => option.name}
                value={formData.goldsmith_id ? (goldsmiths || []).find(g => g.id === formData.goldsmith_id) : null}
                onChange={handleGoldsmithChange}
                disabled={dialogMode === 'view'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Goldsmith"
                    required
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Business />
                        </InputAdornment>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">{option.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.contact_person} • {option.phone}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Product Name"
                name="product_name"
                value={formData.product_name}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmojiEvents /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Purchase Date"
                name="purchase_date"
                type="date"
                value={formData.purchase_date}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><CalendarToday /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Net Weight (g)"
                name="net_weight"
                type="number"
                value={formData.net_weight}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                required
                inputProps={{ step: "0.001" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Scale /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Touch"
                name="touch"
                type="number"
                value={formData.touch}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                required
                inputProps={{ step: "0.01", min: 0, max: 100 }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><EmojiEvents /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Gold Rate (₹/g)"
                name="gold_rate"
                type="number"
                value={formData.gold_rate}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                required
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><TrendingUpIcon /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Pure Amount (g)"
                name="pure_amount"
                type="number"
                value={formData.pure_amount}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.001" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Diamond /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="MC/Wastage Amount (₹)"
                name="mc_wastage_amount"
                type="number"
                value={formData.mc_wastage_amount}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>
                }}
              />
            </Grid>

            {/* Calculations Display */}
            <Grid item xs={12}>
              <Card sx={{ p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Calculate color="primary" />
                  Calculated Values
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Pure Weight</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {calculations.pure_weight.toFixed(3)}g
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Pure Amount</Typography>
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        {formatCurrency(calculations.pure_amount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Total Amount</Typography>
                      <Typography variant="h6" fontWeight="bold" color="success.main">
                        {formatCurrency(calculations.total_amount)}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" color="text.secondary">Balance</Typography>
                      <Typography variant="h6" fontWeight="bold" color="warning.main">
                        {formatCurrency(calculations.balance_amount)}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Making Charges"
                name="making_charges"
                type="number"
                value={formData.making_charges}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Speed /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Wastage Charges"
                name="wastage_charges"
                type="number"
                value={formData.wastage_charges}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><TrendingDownIcon /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Other Charges"
                name="other_charges"
                type="number"
                value={formData.other_charges}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }}>
                <Typography variant="h6" color="text.secondary">
                  Payment Details
                </Typography>
              </Divider>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" disabled={dialogMode === 'view'}>
                <Typography variant="subtitle1" gutterBottom>Payment Mode</Typography>
                <RadioGroup
                  name="payment_mode"
                  value={formData.payment_mode}
                  onChange={handleInputChange}
                  row
                >
                  {paymentModes.map((mode) => (
                    <FormControlLabel
                      key={mode.value}
                      value={mode.value}
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {mode.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {mode.description}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            </Grid>

            {formData.payment_mode !== 'amount_only' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Pure Given (g)"
                  name="pure_given"
                  type="number"
                  value={formData.pure_given}
                  onChange={handleInputChange}
                  disabled={dialogMode === 'view'}
                  inputProps={{ step: "0.001" }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Diamond /></InputAdornment>
                  }}
                />
              </Grid>
            )}

            {formData.payment_mode !== 'pure_only' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount Paid (₹)"
                  name="amount_paid"
                  type="number"
                  value={formData.amount_paid}
                  onChange={handleInputChange}
                  disabled={dialogMode === 'view'}
                  inputProps={{ step: "0.01" }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><LocalAtm /></InputAdornment>
                  }}
                />
              </Grid>
            )}

            {/* Balance Fields */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Balance Pure (g)"
                name="balance_pure"
                type="number"
                value={formData.balance_pure}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.001" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><Diamond /></InputAdornment>
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Balance Amount (₹)"
                name="balance_amount"
                type="number"
                value={formData.balance_amount}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                inputProps={{ step: "0.01" }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><AttachMoney /></InputAdornment>
                }}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notes"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                disabled={dialogMode === 'view'}
                multiline
                rows={3}
                placeholder="Additional notes or special instructions"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ borderRadius: 2, px: 3 }}
          >
            {dialogMode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {dialogMode !== 'view' && (
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                borderRadius: 2,
                px: 4,
                background: 'linear-gradient(135deg, #ff9800 0%, #ffc107 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #f57c00 0%, #ff9800 100%)',
                }
              }}
            >
              {dialogMode === 'create' ? 'Create Purchase' : 'Update Purchase'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Cancel Purchase Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Purchase</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to cancel purchase <strong>#{selectedPurchase?.id}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. The purchase will be marked as cancelled and removed from active records.
          </Typography>
          {selectedPurchase && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">Purchase Details:</Typography>
              <Typography variant="body2">
                <strong>Goldsmith:</strong> {selectedPurchase.goldsmith?.name || 'N/A'}<br/>
                <strong>Product:</strong> {selectedPurchase.product_name || 'N/A'}<br/>
                <strong>Amount:</strong> ₹{Number(selectedPurchase.total_amount || 0).toFixed(2)}<br/>
                <strong>Status:</strong> {selectedPurchase.status}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Keep Purchase</Button>
          <Button 
            onClick={handleCancelConfirm} 
            variant="contained"
            color="error"
          >
            Cancel Purchase
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoldsmithPurchases;
